/**
 * Global XHR API Utility - XMLHttpRequest-based API calls to avoid rrweb-recorder interference
 * This utility MUST be used by ALL components for API calls to prevent clone errors
 */

import { API_URL } from '../config/api';

// XMLHttpRequest-based request that is immune to rrweb-recorder interference
export const xhrFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    xhr.open(options.method || 'GET', fullUrl);
    xhr.withCredentials = true;
    
    // Check if this is a binary/blob request (for downloads)
    const isDownload = options.responseType === 'blob' || 
                       fullUrl.includes('/direct-download/') || 
                       fullUrl.includes('/download/');
    
    if (isDownload) {
      xhr.responseType = 'blob';
    }
    
    // Set headers (auth via httpOnly cookie — no Authorization header needed)
    const headers = {
      ...(options.body && typeof options.body === 'string' && { 'Content-Type': 'application/json' }),
      ...options.headers,
    };
    
    Object.keys(headers).forEach(key => {
      if (headers[key]) {
        xhr.setRequestHeader(key, headers[key]);
      }
    });
    
    xhr.onload = () => {
      let data;
      let blobData = null;
      
      if (isDownload) {
        // For downloads, keep the blob response
        blobData = xhr.response;
        data = null;
      } else {
        try {
          data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
        } catch (e) {
          data = xhr.responseText || {};
        }
      }
      
      // Create a response-like object with blob support
      const response = {
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        statusText: xhr.statusText,
        data: data,
        _blob: blobData,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(isDownload ? '' : xhr.responseText),
        blob: () => Promise.resolve(blobData)
      };
      
      resolve(response);
    };
    
    xhr.onerror = () => {
      reject(new Error('Network error'));
    };
    
    xhr.ontimeout = () => {
      reject(new Error('Request timeout'));
    };
    
    xhr.timeout = options.timeout || 30000;
    
    // Handle body
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

// Simple GET request that returns JSON directly
export const xhrGet = async (url, options = {}) => {
  const response = await xhrFetch(url, { ...options, method: 'GET' });
  return response.data;
};

// Simple POST request that returns JSON directly  
export const xhrPost = async (url, data, options = {}) => {
  const response = await xhrFetch(url, { 
    ...options, 
    method: 'POST',
    body: JSON.stringify(data)
  });
  return response.data;
};

// File upload using XMLHttpRequest
export const xhrUpload = (url, formData, onProgress = null) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const fullUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
    xhr.open('POST', fullUrl);
    xhr.withCredentials = true;
    
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }
    
    xhr.onload = () => {
      let data;
      try {
        data = xhr.responseText ? JSON.parse(xhr.responseText) : {};
      } catch (e) {
        data = { detail: 'Invalid response' };
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data);
      } else {
        reject(new Error(data.detail || `Upload failed with status ${xhr.status}`));
      }
    };
    
    xhr.onerror = () => reject(new Error('Network error'));
    xhr.ontimeout = () => reject(new Error('Upload timeout'));
    xhr.timeout = 120000; // 2 minute timeout for uploads
    xhr.send(formData);
  });
};

// Override global fetch to use XHR (for components that can't be easily modified)
if (typeof window !== 'undefined') {
  const originalFetch = window.fetch;
  
  // Store original fetch for direct access when needed
  window._originalFetch = originalFetch;
  
  window.fetch = async (url, options = {}) => {
    // Only intercept API calls to our backend
    const urlStr = typeof url === 'string' ? url : url.toString();
    if (urlStr.includes('/api/') || urlStr.includes(API_URL)) {
      try {
        const response = await xhrFetch(urlStr, options);
        // Return a fetch-like response with full blob support
        return {
          ok: response.ok,
          status: response.status,
          statusText: response.statusText,
          json: () => Promise.resolve(response.data),
          text: () => response.text(),
          blob: () => response.blob(),
          clone: function() { return this; }
        };
      } catch (error) {
        throw error;
      }
    }
    // For non-API calls, use original fetch
    return originalFetch(url, options);
  };
}

export default { xhrFetch, xhrGet, xhrPost, xhrUpload };
